import type { ReactNode } from 'react';
import { Text as RNText } from 'react-native';
import { twMerge } from 'tailwind-merge';

/**
 * The expert profile type scale.
 *
 * Every profile surface (the profile page and the inquiry sidebar) renders text
 * through these role components so typography stays cohesive and cannot drift.
 * Each component owns only the canonical font, size, weight, and color. Pass
 * layout-only classes (margins, leading, alignment) via `className`. See
 * `.claude/rules/profile.md`.
 *
 * Roles:
 * - ProfileSectionTitle: section heading, e.g. "Work History". Green (primary).
 * - ProfileItemTitle: an item's primary line, e.g. a job title, degree, or
 *   research title. Green (primary), so every section's items read the same.
 * - ProfileItemMeta: muted secondary line under a title, e.g. a date range,
 *   organization, institution, "journal · year", or "phase · status".
 * - ProfileBodyText: free-text body, e.g. an about summary or disclosure detail.
 *
 * Clickable titles (publication and trial names) stay as `Link`/`LinkText` in
 * primary-600, since green is reserved for headings and links.
 */

interface ProfileTextProps {
  children: ReactNode;
  className?: string;
}

export function ProfileSectionTitle({ children, className }: ProfileTextProps) {
  return (
    <RNText className={twMerge('font-semibold text-base text-primary-600', className)}>
      {children}
    </RNText>
  );
}

export function ProfileItemTitle({ children, className }: ProfileTextProps) {
  return (
    <RNText className={twMerge('font-medium text-base text-primary-600', className)}>
      {children}
    </RNText>
  );
}

export function ProfileItemMeta({
  children,
  className,
  numberOfLines,
  testID,
}: ProfileTextProps & { numberOfLines?: number; testID?: string }) {
  return (
    <RNText
      testID={testID}
      numberOfLines={numberOfLines}
      className={twMerge('font-body text-sm text-typography-800', className)}>
      {children}
    </RNText>
  );
}

export function ProfileBodyText({ children, className }: ProfileTextProps) {
  return (
    <RNText
      className={twMerge('font-body text-base leading-relaxed text-typography-800', className)}>
      {children}
    </RNText>
  );
}
